import { useState, useEffect } from "react";
import { cn } from "@/lib/utils";
import DualScoreVisualization from "./DualScoreVisualization";

import PlatformCard from "./PlatformCard";
import ActionPlan from "./ActionPlan";
import { useTechnicalChecks } from "@/hooks/useTechnicalChecks";
import DetailedMethodology from "./DetailedMethodology";
import EvidenceBox from "./EvidenceBox";

import ResultsFAQ from "./ResultsFAQ";
import Confetti from "./Confetti";

import ExecutiveSummary from "./results/ExecutiveSummary";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Sparkles, CheckCircle2, XCircle, ExternalLink, HelpCircle, Brain, TrendingUp, Lock, GraduationCap, Wrench, AlertTriangle, Info } from "lucide-react";
import { ResponsiveTooltip } from "@/components/ui/responsive-tooltip";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import type { VisibilityAnalysis } from "@/lib/api/ai-visibility";
import type { PlatformResult } from "@/lib/api/platform-visibility";
import type { GoogleSearchResult } from "@/lib/api/google-search-visibility";
import { trackScanCompleted, trackPageView } from "@/lib/analytics";
import type { ScanStatus } from "@/hooks/useScanProgress";
import { calculateVisibilityScore } from "@/lib/scoring";
import { cite, citeWithUrl } from "@/data/research-sources";

const yangSource = citeWithUrl('yang-2025');
const kirstenSource = citeWithUrl('kirsten-2025');

interface ScanResultsProps {
  analysis: VisibilityAnalysis;
  brandName: string;
  keyword?: string;
  platformResults?: PlatformResult[];
  googleResult?: GoogleSearchResult | null;
  websiteUrl?: string;
  chatGptStatus?: ScanStatus;
  googleStatus?: ScanStatus;
  isFromCache?: boolean;
  // V2.1 Discovery Mode fields
  aiScore?: number;
  queryType?: 'discovery' | 'fallback' | 'not_found' | 'error';
  queryUsed?: string;
  rawResponseSnippet?: string;
  competitorMentions?: string[];
}

const ScanResults = ({ 
  analysis, 
  brandName, 
  keyword, 
  platformResults, 
  googleResult, 
  websiteUrl, 
  chatGptStatus = "success", 
  googleStatus = "success", 
  isFromCache = false,
  // V2.1 Discovery Mode fields
  aiScore,
  queryType,
  queryUsed,
  rawResponseSnippet,
  competitorMentions,
}: ScanResultsProps) => {
  const [showConfetti, setShowConfetti] = useState(false);
  const [showGoogleModal, setShowGoogleModal] = useState(false);
  const [showScoringModal, setShowScoringModal] = useState(false);
  const [showAIPlatformsModal, setShowAIPlatformsModal] = useState(false);
  const { toast } = useToast();


  const techState = useTechnicalChecks(websiteUrl);

  // Get AI Platforms result from API if available (previously ChatGPT)
  const aiPlatformsResult = platformResults?.find(p => p.platform === "ChatGPT") || {
    platform: "AI Platforms",
    found: false,
    context: "Not scanned",
    confidence: "low" as const,
  };

  // Get Google Search result
  const googleSearchResult = googleResult || {
    platform: "Google Search",
    found: false,
    context: "Not scanned",
    confidence: "low" as const,
    position: null as number | null,
    spellingCorrected: false,
    suggestedQuery: undefined as string | undefined,
  };

  // Calculate comprehensive score using new algorithm
  const scoring = calculateVisibilityScore({
    aiScore: aiScore, // V2.1: Pass explicit aiScore from Discovery Mode
    aiPlatformsFound: aiPlatformsResult.found,
    googleFound: googleSearchResult.found,
    googlePosition: googleSearchResult.position || null,
    googleConfidence: googleSearchResult.confidence,
    isPaidUser: false, // Automated scan - max 70/100
  });

  const weightedScore = scoring.currentScore;

  // Trigger confetti

  // Trigger confetti for high scores, track scan completed, and update URL for analytics
  useEffect(() => {
    // Update URL to /results for analytics separation (shallow routing)
    // Skip if already on a /scan/:id shareable URL
    if (window.history && window.location.pathname !== '/results' && !window.location.pathname.startsWith('/scan/')) {
      window.history.pushState({ results: true }, '', '/results');
      // Trigger manual page view for GA4 and Clarity
      trackPageView('/results', `AI Visibility Results - ${brandName}`);
    }

    // Track scan completed event
    trackScanCompleted(
      brandName,
      keyword || '',
      weightedScore,
      aiPlatformsResult.found,
      googleSearchResult.found,
      websiteUrl
    );
    
    // Set flag that user completed a scan (for methodology page protection)
    sessionStorage.setItem('ailens_scan_completed', 'true');
    sessionStorage.setItem('ailens_scan_timestamp', Date.now().toString());
    
    // Confetti only for truly exceptional scores (which won't happen on automated scan due to max 70)
    // But keep the logic in case extended analysis is added later
    if (scoring.currentScore >= 90) {
      setTimeout(() => setShowConfetti(true), 1500);
    }
  }, [brandName, keyword, weightedScore, aiPlatformsResult.found, googleSearchResult.found]);


  // Scoring Methodology Modal - Updated for V2.1
  const ScoringMethodologyModal = () => (
    <Dialog open={showScoringModal} onOpenChange={setShowScoringModal}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl">
            How Your Score is Calculated
          </DialogTitle>
          <DialogDescription>
            Transparent, research-backed methodology
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6 text-sm">
          {/* Formula */}
          <section className="p-4 bg-muted/50 border">
            <h4 className="font-semibold mb-3 text-center">The Formula</h4>
            <div className="text-xl font-mono text-center mb-4">
              Score = ChatGPT (40) + Google (30) + Additional Platforms (30)
            </div>
            <p className="text-xs text-muted-foreground text-center">
              Points earned based on platform visibility
            </p>
          </section>

          {/* Components */}
          <section>
            <h4 className="font-semibold mb-3">Score Components</h4>
            <div className="space-y-3">
              <div className="p-3 border bg-muted/30 space-y-3">
                <div className="flex items-start gap-3">
                  <Brain className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <div className="font-medium">ChatGPT Visibility (40 pts)</div>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Does AI <strong>recommend</strong> your brand? Discovery mode tests real recommendations.
                    </p>
                  </div>
                </div>
                
                {/* AI Discovery Levels breakdown */}
                <div className="space-y-1.5 pl-8">
                  <p className="text-xs font-medium text-foreground">AI Discovery Levels:</p>
                  <div className={cn(
                    "flex items-center justify-between text-xs px-2 py-1.5 rounded",
                    aiScore === 40 ? "bg-success/15 border border-success/30 font-medium" : ""
                  )}>
                    <span className={aiScore === 40 ? "text-foreground" : "text-muted-foreground"}>Recommended in discovery queries</span>
                    <span className={cn("font-mono", aiScore === 40 ? "text-success" : "text-muted-foreground")}>+40 points</span>
                  </div>
                  <div className={cn(
                    "flex items-center justify-between text-xs px-2 py-1.5 rounded",
                    aiScore === 20 ? "bg-warning/15 border border-warning/30 font-medium" : ""
                  )}>
                    <span className={aiScore === 20 ? "text-foreground" : "text-muted-foreground"}>Recognized, not yet recommended</span>
                    <span className={cn("font-mono", aiScore === 20 ? "text-warning" : "text-muted-foreground")}>+20 points</span>
                  </div>
                  <div className={cn(
                    "flex items-center justify-between text-xs px-2 py-1.5 rounded",
                    aiScore === 0 ? "bg-muted/50 border border-border font-medium" : ""
                  )}>
                    <span className={aiScore === 0 ? "text-foreground" : "text-muted-foreground"}>Not found by AI</span>
                    <span className="font-mono text-muted-foreground">0 points</span>
                  </div>
                </div>
                
                <p className="text-xs text-muted-foreground pl-8 leading-relaxed">
                  We test two things: First, a discovery query: <strong className="text-foreground">&ldquo;What are the best {keyword || 'your category'} options?&rdquo;</strong> to see if AI recommends your brand. If not found, a recognition query: <strong className="text-foreground">&ldquo;Tell me about {brandName}&rdquo;</strong> to check if AI knows your brand. Being recognized but not recommended is common, and it&apos;s your biggest optimization opportunity.
                </p>
              </div>
              <div className="p-3 border bg-muted/30 space-y-3">
                <div className="flex items-start gap-3">
                  <TrendingUp className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <div className="font-medium">Google Search (30 pts)</div>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Does your domain rank for your keyword? We check your Google position for generic keyword searches.
                    </p>
                  </div>
                </div>
                
                {/* Google Position Rewards breakdown */}
                <div className="space-y-1.5 pl-8">
                  <p className="text-xs font-medium text-foreground">Google Position Rewards:</p>
                  <div className={cn(
                    "flex items-center justify-between text-xs px-2 py-1.5 rounded",
                    scoring.googleAdjustedScore === 30 ? "bg-success/15 border border-success/30 font-medium" : ""
                  )}>
                    <span className={scoring.googleAdjustedScore === 30 ? "text-foreground" : "text-muted-foreground"}>Position 1-3 (AI citation zone)</span>
                    <span className={cn("font-mono", scoring.googleAdjustedScore === 30 ? "text-success" : "text-muted-foreground")}>+30 points</span>
                  </div>
                  <div className={cn(
                    "flex items-center justify-between text-xs px-2 py-1.5 rounded",
                    scoring.googleAdjustedScore === 20 ? "bg-warning/15 border border-warning/30 font-medium" : ""
                  )}>
                    <span className={scoring.googleAdjustedScore === 20 ? "text-foreground" : "text-muted-foreground"}>Position 4-10 (Page 1)</span>
                    <span className={cn("font-mono", scoring.googleAdjustedScore === 20 ? "text-warning" : "text-muted-foreground")}>+20 points</span>
                  </div>
                  <div className={cn(
                    "flex items-center justify-between text-xs px-2 py-1.5 rounded",
                    scoring.googleAdjustedScore === 10 ? "bg-warning/15 border border-warning/30 font-medium" : ""
                  )}>
                    <span className={scoring.googleAdjustedScore === 10 ? "text-foreground" : "text-muted-foreground"}>Position 11-20</span>
                    <span className={cn("font-mono", scoring.googleAdjustedScore === 10 ? "text-warning" : "text-muted-foreground")}>+10 points</span>
                  </div>
                  <div className={cn(
                    "flex items-center justify-between text-xs px-2 py-1.5 rounded",
                    scoring.googleAdjustedScore === 0 ? "bg-muted/50 border border-border font-medium" : ""
                  )}>
                    <span className={scoring.googleAdjustedScore === 0 ? "text-foreground" : "text-muted-foreground"}>Position 21+ or not found</span>
                    <span className="font-mono text-muted-foreground">0 points</span>
                  </div>
                </div>
                
                <p className="text-xs text-muted-foreground pl-8 leading-relaxed">
                  Top 20 domains capture 67% of all AI citations. Higher positions = more AI visibility.
                  <a href={yangSource.url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline ml-1">({yangSource.text})</a>
                </p>
                <p className="text-xs text-muted-foreground italic pl-8 mt-1">
                  Google results reflect US-based searches (google.com) for benchmark consistency.
                </p>
              </div>
              <div className="w-full flex items-start gap-3 p-3 border border-dashed bg-muted/50 opacity-70">
                <Lock className="h-5 w-5 text-muted-foreground flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <div className="font-medium text-muted-foreground">Additional Platforms (30 pts)</div>
                    <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded flex items-center gap-1"><Lock className="w-3 h-3" /> Not included</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Additional AI platforms not included in the automated scan.
                  </p>
                </div>
              </div>
            </div>
          </section>

          <div className="mt-6 pt-4 border-t">
            <p className="text-sm text-muted-foreground mb-2">
              Want to learn more about our methodology?
            </p>
            <a 
              href="/methodology"
              className="inline-flex items-center gap-2 text-sm text-primary hover:underline font-medium"
            >
              View Full Methodology
              <ExternalLink className="h-4 w-4" />
            </a>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );

  // AI Platforms Modal
  const AIPlatformsModal = () => (
    <Dialog open={showAIPlatformsModal} onOpenChange={setShowAIPlatformsModal}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl">
            ChatGPT Visibility
          </DialogTitle>
          <DialogDescription>
            How we check your brand presence on ChatGPT
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 text-sm">
          <section>
            <h4 className="font-semibold text-base mb-2">What We Check</h4>
            <p className="text-muted-foreground">
              We analyze if your brand appears when ChatGPT generates responses 
              to queries related to your industry and keywords.
            </p>
          </section>

          <section>
            <h4 className="font-semibold text-base mb-2">Platforms Analyzed</h4>
            <div className="space-y-3">
              <div className="flex items-start gap-2 p-3 border bg-muted/30">
                <div className="font-medium">ChatGPT</div>
                <div className="ml-auto">
                  <span className="text-xs bg-success/10 text-success px-2 py-1 border border-success/20">
                    Active
                  </span>
                </div>
              </div>
              <div className="flex items-start gap-2 p-3 border bg-muted/30">
                <div>
                  <div className="font-medium">Additional AI Platforms</div>
                   <p className="text-xs text-muted-foreground mt-1">
                    Not included in the automated scan
                  </p>
                </div>
                <div className="ml-auto">
                  <Lock className="h-4 w-4 text-muted-foreground" />
                </div>
              </div>
            </div>
          </section>

          <section>
            <h4 className="font-semibold text-base mb-2">Why It Matters</h4>
            <p className="text-muted-foreground">
              AI platforms are becoming primary discovery channels. When someone asks 
              ChatGPT for recommendations in your space, you want your brand to appear 
              in the response. This directly impacts brand awareness and consideration.
            </p>
          </section>

          <section>
            <h4 className="font-semibold text-base mb-2">Scoring Weight</h4>
            <p className="text-muted-foreground">
              ChatGPT visibility accounts for <strong>40% of your total score</strong>. 
              Combined with Google Search (30%) and additional platforms (30%), this creates 
              a complete picture of your AI visibility.
            </p>
          </section>

          <div className="mt-6 pt-4 border-t">
            <p className="text-sm text-muted-foreground mb-2">
              Want to learn more about our methodology?
            </p>
            <a 
              href="/methodology"
              className="inline-flex items-center gap-2 text-sm text-primary hover:underline font-medium"
            >
              View Full Methodology
              <ExternalLink className="h-4 w-4" />
            </a>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );

  // Google Search Visibility Modal
  const GoogleVisibilityModal = () => (
    <Dialog open={showGoogleModal} onOpenChange={setShowGoogleModal}>
      <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl">Google Search Visibility</DialogTitle>
          <DialogDescription>
            How we check your Google ranking and why it matters
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-5 pt-2">
          <div className="space-y-2">
            <h4 className="font-semibold text-foreground">What We Check</h4>
            <p className="text-sm text-muted-foreground">
              We search Google for your keyword (e.g., "{keyword || 'your keyword'}") and verify if YOUR domain appears 
              in the top 10 organic results. This is a generic search without your brand name, measuring true competitive visibility.
            </p>
          </div>

          <div className="space-y-2">
            <h4 className="font-semibold text-foreground">Results Meaning</h4>
            <div className="space-y-2">
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-success mt-0.5 shrink-0" />
                <p className="text-sm text-muted-foreground">
                  <span className="font-medium text-success">Found:</span> Your domain 
                  appears in Google's top 10 results
                </p>
              </div>
              <div className="flex items-start gap-2">
                <XCircle className="w-4 h-4 text-destructive mt-0.5 shrink-0" />
                <p className="text-sm text-muted-foreground">
                  <span className="font-medium text-destructive">Not Found:</span> Either 
                  you're not ranking yet, or Google doesn't recognize your brand 
                  name (may autocorrect it)
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <h4 className="font-semibold text-foreground">Why It Matters</h4>
            <p className="text-sm text-muted-foreground">
              Academic research confirms AI platforms use different selection criteria than 
              Google. While ~60% of AI citations overlap with top Google results, 
              <span className="font-medium text-foreground"> ~40% come from sources outside the top 10</span>, making separate 
              AI visibility tracking essential.
            </p>
            <p className="text-xs text-muted-foreground mt-2">
              Source: <a href={kirstenSource.url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">{kirstenSource.text}</a>
            </p>
          </div>

          <div className="h-px bg-border" />

          {/* Research Foundation */}
          <div className="mt-6 space-y-3">
            <p className="text-sm font-medium">Research Foundation</p>
            
            {/* Source 1: Yang arXiv 2025 */}
            <div className="flex items-start gap-2">
              <ExternalLink className="h-4 w-4 text-primary flex-shrink-0 mt-1" />
              <div>
                <a 
                  href={yangSource.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline font-medium text-sm"
                >
                  {yangSource.text.replace(', ', ' (')}){/* Yang (arXiv 2025) */}
                </a>
                <p className="text-xs text-muted-foreground mt-1">
                  Top 20 domains capture 67% of all AI citations
                </p>
              </div>
            </div>

            {/* Source 2: Kirsten et al. 2025 */}
            <div className="flex items-start gap-2">
              <ExternalLink className="h-4 w-4 text-primary flex-shrink-0 mt-1" />
              <div>
                <a 
                  href={kirstenSource.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline font-medium text-sm"
                >
                  {kirstenSource.text.replace(', ', ' (')}){/* Kirsten et al. (arXiv 2025) */}
                </a>
                <p className="text-xs text-muted-foreground mt-1">
                  ~40% of AI-cited sources don't appear in Google's top organic results
                </p>
              </div>
            </div>

            {/* Source 2: Semrush July 2025 */}
            <div className="flex items-start gap-2">
              <ExternalLink className="h-4 w-4 text-primary flex-shrink-0 mt-1" />
              <div>
                <a 
                  href="https://www.semrush.com/blog/ai-mode-comparison-study/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline font-medium text-sm"
                >
                  Semrush (July 2025)
                </a>
                <p className="text-xs text-muted-foreground mt-1">
                  Research shows significant overlap between AI platform citations and Google's top results
                </p>
              </div>
            </div>
          </div>

          <div className="pt-2 border-t border-border">
            <p className="text-xs text-muted-foreground text-center">
              Want to learn more about our methodology?{" "}
              <a href="/methodology" className="text-primary hover:underline">
                View Full Methodology →
              </a>
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );

  return (
    <>
      <Confetti trigger={showConfetti} />
      
      {/* Modals */}
      <ScoringMethodologyModal />
      <AIPlatformsModal />
      <GoogleVisibilityModal />

      <div className="w-full max-w-full md:max-w-5xl mx-auto px-4 md:px-6">
        <Card className="w-full bg-card border-2 border-border rounded-none shadow-sm">
          <CardContent className="pt-8 md:pt-12 px-5 md:px-8 space-y-6 md:space-y-8">
            {/* Score Section with localized glow */}
            <div className="relative results-entrance" style={{ "--entrance-delay": "0ms" } as React.CSSProperties}>
              <div className="hero-glow absolute inset-0 pointer-events-none -m-8" aria-hidden="true" />
              <div className="relative">
                <DualScoreVisualization 
                  scoring={scoring}
                  brandName={brandName}
                  websiteUrl={websiteUrl}
                  skipAnimation={isFromCache}
                  isLoading={chatGptStatus === "loading" || googleStatus === "loading"}
                  googlePosition={googleSearchResult.position || null}
                />
              </div>
            </div>

            {/* Executive Summary */}
            {aiScore !== undefined && (
              <div className="results-entrance" style={{ "--entrance-delay": "50ms" } as React.CSSProperties}>
                <ExecutiveSummary
                  aiScore={aiScore}
                  totalScore={scoring.currentScore}
                  brandName={brandName}
                  keyword={keyword || ''}
                  competitorMentions={competitorMentions}
                  googlePosition={googleSearchResult.position || null}
                  queryUsed={queryUsed || ''}
                />
              </div>
            )}

            {/* Inline AI score context + How is this calculated — AFTER Pro CTA */}
            <div className="results-entrance flex flex-col items-center gap-2 my-4 sm:my-6" style={{ "--entrance-delay": "120ms" } as React.CSSProperties}>
              <button
                
                onClick={() => setShowScoringModal(true)}
                className="text-sm text-muted-foreground hover:text-foreground px-4 py-1.5 transition-all cursor-pointer flex items-center gap-1.5 mx-auto border border-border hover:border-primary/40 hover:bg-muted/50 rounded-full"
              >
                <Info className="h-4 w-4 flex-shrink-0" />
                How is this calculated?
              </button>
            </div>

            {/* V2.1 Evidence Box */}
            {queryType && queryUsed && rawResponseSnippet && (
              <div className="results-entrance" style={{ "--entrance-delay": "200ms" } as React.CSSProperties}>
                <EvidenceBox
                  queryUsed={queryUsed}
                  rawResponseSnippet={rawResponseSnippet}
                  brandName={brandName}
                  queryType={queryType}
                  aiScore={aiScore}
                  competitorMentions={competitorMentions}
                />
              </div>
            )}

            {/* Platform Results Header with Academic Tooltip */}
            <div className="results-entrance flex items-center gap-2" style={{ "--entrance-delay": "300ms" } as React.CSSProperties}>
              <h3 className="text-lg font-semibold text-foreground">Platform Analysis</h3>
              <ResponsiveTooltip
                content={
                  <div className="max-w-xs space-y-1">
                    <p className="text-sm font-medium">Top 20 domains capture 67% of all AI citations</p>
                    <p className="text-xs text-muted-foreground">Source: {yangSource.text}</p>
                  </div>
                }
                side="top"
              >
                <GraduationCap className="h-4 w-4 text-primary/70 hover:text-primary transition-colors cursor-help" />
              </ResponsiveTooltip>
            </div>

            {/* Platform Results */}
            <div className="results-entrance grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4" style={{ "--entrance-delay": "400ms" } as React.CSSProperties}>
              <div>
                <PlatformCard 
                  name="ChatGPT" 
                  found={aiPlatformsResult.found}
                  context={aiPlatformsResult.context}
                  confidence={aiPlatformsResult.confidence}
                  status={chatGptStatus}
                  skipAnimation={isFromCache}
                  infoTooltip="Does ChatGPT recommend your brand?"
                  onLearnMore={() => setShowAIPlatformsModal(true)}
                />
              </div>
              <div>
                <PlatformCard
                  name="Google Search" 
                  found={googleSearchResult.found}
                  context={googleSearchResult.context}
                  confidence={googleSearchResult.confidence}
                  status={googleStatus}
                  skipAnimation={isFromCache}
                  infoTooltip={`SEO yields 7× more visibility than GEO-only (${cite('neurips-2025')})`}
                  spellingCorrected={googleSearchResult.spellingCorrected}
                  suggestedQuery={googleSearchResult.suggestedQuery}
                  onLearnMore={() => setShowGoogleModal(true)}
                />
              </div>
            </div>

            {/* AI-Readiness Action Plan */}
            <div className="results-entrance" style={{ "--entrance-delay": "500ms" } as React.CSSProperties}>
              <ActionPlan websiteUrl={websiteUrl} techStatus={techState.status} techData={techState.data} />
            </div>

            {/* Detailed Methodology */}
            <div className="results-entrance" style={{ "--entrance-delay": "600ms" } as React.CSSProperties}>
              <DetailedMethodology />
            </div>

            {/* FAQ Section */}
            <div className="results-entrance" style={{ "--entrance-delay": "650ms" } as React.CSSProperties}>
              <ResultsFAQ />
            </div>

          </CardContent>
        </Card>
      </div>
    </>
  );
};

export default ScanResults;
